'use client';

import { useEffect, useRef, useState } from 'react';

import {
  getBasket,
  getCheckoutState,
  rememberPendingOrderNumber,
  setLastPaymentType,
} from '@/lib/BasketStorage';
import { CheckoutFormField } from '@/lib/CheckoutFormField';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { reportCheckoutBError } from '@/lib/reportCheckoutBError';
import { getBasketEcommerceBody, GTMEventType } from '@/lib/GTMEventType';
import { getIsAnonymous } from '@/lib/LocalStorage';
import { Requester } from '@/lib/Requester';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { GenericResponse } from '@/models/GenericResponse';
import { IyzicoSavedCard, OrderPaymentInformation, OrderResponse } from '@/models/Order';
import { PaymentType } from '@/models/PaymentType';
import clsx from 'clsx';
import InnerHTML from 'dangerously-set-html-content';
import { Trash } from 'lucide-react';
import { CheckoutPaymentProblem } from './CheckoutPaymentProblem';
import PrivacyDialog from './PrivacyDialog';
import { SavedCardBrandIcon, savedCardTitle } from './SavedCardBrand';
import {
  getPreferManualCard,
  IYZICO_PREFER_MANUAL_CARD_KEY,
  SavedCardsLoadFailedDialog,
  setPreferManualCard,
} from './SavedCardsLoadFailedDialog';
import MasterpassCardForm from './modals/MasterpassCardForm';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Spinner } from './ui/spinner';
import { useToast } from './ui/use-toast';

function logIyzico(
  checkoutB: boolean | undefined,
  action: string,
  message: string,
  kind: 'api' | 'validation' | 'unexpected' = 'api',
  error?: unknown,
) {
  reportCheckoutBError({
    flow: checkoutB ? 'checkout-b' : 'checkout-a',
    step: 'payment',
    action,
    kind,
    message,
    error,
  });
}

function decodeThreeDSHtml(content: string): string {
  try {
    if (typeof window !== 'undefined' && typeof window.atob === 'function') {
      return decodeURIComponent(
        Array.prototype.map
          .call(
            window.atob(content),
            (c: string) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2),
          )
          .join(''),
      );
    }
  } catch {
    // fall through
  }
  try {
    return atob(content);
  } catch {
    return content;
  }
}

export function CheckoutPaymentTypeIyzico({
  paymentType,
  paymentInformation,
  setPaymentInformation,
  total,
  paymentTypes,
  flow,
  checkoutB,
  milesHoldClosed,
}: {
  paymentType: number;
  paymentInformation?: OrderPaymentInformation;
  setPaymentInformation: (data: OrderPaymentInformation) => void;
  total?: number;
  paymentTypes: PaymentType[];
  flow?: 'checkout_form' | 'api_3ds';
  checkoutB?: boolean;
  milesHoldClosed?: boolean;
}) {
  const effectiveFlow = flow === 'api_3ds' ? 'api_3ds' : 'checkout_form';

  if (effectiveFlow === 'api_3ds') {
    return (
      <IyzicoApi3dsPayment
        checkoutB={checkoutB}
        milesHoldClosed={milesHoldClosed}
        paymentType={paymentType}
        paymentInformation={paymentInformation}
        setPaymentInformation={setPaymentInformation}
        paymentTypes={paymentTypes}
      />
    );
  }

  return (
    <IyzicoCheckoutFormPayment
      checkoutB={checkoutB}
      milesHoldClosed={milesHoldClosed}
      paymentType={paymentType}
      paymentInformation={paymentInformation}
      setPaymentInformation={setPaymentInformation}
      total={total}
      paymentTypes={paymentTypes}
    />
  );
}

function IyzicoCheckoutFormPayment({
  paymentType,
  paymentInformation,
  setPaymentInformation,
  total,
  paymentTypes,
  checkoutB,
  milesHoldClosed,
}: {
  paymentType: number;
  paymentInformation?: OrderPaymentInformation;
  setPaymentInformation: (data: OrderPaymentInformation) => void;
  total?: number;
  paymentTypes: PaymentType[];
  checkoutB?: boolean;
  milesHoldClosed?: boolean;
}) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentData, setPaymentData] = useState<React.ReactNode | null>(null);
  const [privacyChecked, setPrivacyChecked] = useState(Boolean(checkoutB));
  const [isPrivacyDialogOpen, setPrivacyDialogOpen] = useState(false);

  useEffect(() => {
    async function startOrder() {
      if (milesHoldClosed || isLoading || paymentInformation) return;

      setIsLoading(true);
      try {
        const body = { paymentType, paymentInformation: {} };
        const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
          Endpoints.START_ORDER,
          body,
        );
        if (result.data.order.paymentInformation) {
          setPaymentInformation(result.data.order.paymentInformation);
        }
      } catch (error) {
        const { message } = ErrorHandler.getMessage(error);
        logIyzico(checkoutB, 'iyzico-start', message, 'api', error);
        toast({
          variant: 'destructive',
          description: message,
        });
      } finally {
        setIsLoading(false);
      }
    }

    startOrder();
  }, [paymentType, milesHoldClosed]);

  useEffect(() => {
    if (window?.iyziInit?.price && window.iyziInit.price !== total) {
      window.iyziInit = undefined;
    }

    if (paymentInformation?.request?.checkoutFormContent) {
      const scriptContainer = document.getElementById('checkout-form-container');
      if (scriptContainer) {
        setPaymentData(paymentInformation?.request?.checkoutFormContent);
      }

      if (window?.iyziInit?.createTag) {
        window.iyziInit.createTag();
      }
    }
  }, [paymentInformation]);

  useEffect(() => {
    function handlePaymentButtonClick() {
      const basket = getBasket();
      setLastPaymentType('Kredi Kartı - iyzico');
      if (basket) {
        sendGTMEvent({
          event: GTMEventType.ADD_PAYMENT_INFO,
          ecommerce: {
            ...getBasketEcommerceBody(basket),
            payment_type: 'Kredi Kartı - iyzico',
          },
        });
      }
    }

    function setupPaymentButtonListener() {
      const paymentButton = document.getElementById('iyz-payment-button');
      if (paymentButton) {
        paymentButton.addEventListener('click', handlePaymentButtonClick);
        return true;
      }
      return false;
    }

    let isListenerAttached = setupPaymentButtonListener();

    const observer = new MutationObserver(() => {
      if (!isListenerAttached) {
        isListenerAttached = setupPaymentButtonListener();
      }
    });

    const formContainer = document.getElementById('checkout-form-container');
    if (formContainer) {
      observer.observe(formContainer, { childList: true, subtree: true });
    }

    return () => {
      const paymentButton = document.getElementById('iyz-payment-button');
      if (paymentButton) {
        paymentButton.removeEventListener('click', handlePaymentButtonClick);
      }
      observer.disconnect();
    };
  }, [paymentType, total]);

  if (milesHoldClosed) return null;

  return (
    <div
      className={`flex flex-col gap-4 min-h-40 ${checkoutB ? 'w-full' : 'sm:gap-8 justify-center pt-6 items-center'}`}
      id="checkout-form-container"
    >
      <div className="flex items-center gap-2">
        <Checkbox
          className="w-4 h-4 flex-shrink-0 "
          checked={privacyChecked}
          onCheckedChange={(isChecked) => setPrivacyChecked(isChecked === true)}
        />
        <span className="text-sm text-hzc_beige_700">
          <span
            className="font-medium hover:underline hover:cursor-pointer"
            onClick={() => setPrivacyDialogOpen(true)}
          >
            Satış Sözleşmesi
          </span>
          &apos;ni okudum ve kabul ediyorum. *
        </span>
        <PrivacyDialog open={isPrivacyDialogOpen} onOpenChange={setPrivacyDialogOpen} />
      </div>
      <div
        className={clsx({
          'opacity-100': checkoutB || privacyChecked,
          'opacity-0': !checkoutB && !privacyChecked,
          'h-0': !checkoutB && !privacyChecked,
          'overflow-hidden': !checkoutB && !privacyChecked,
          'w-full': checkoutB,
        })}
      >
        {!paymentInformation && <Spinner className="size-20" />}
        <InnerHTML
          id="checkout-form"
          className="w-full h-full"
          html={`<div id="iyzipay-checkout-form" class="responsive">${paymentData || ''}</div>`}
          allowRerender={true}
        />
      </div>
      {checkoutB ? null : (
        <CheckoutPaymentProblem paymentType={paymentType} paymentTypes={paymentTypes} />
      )}
    </div>
  );
}

function IyzicoApi3dsPayment({
  paymentType,
  paymentInformation,
  setPaymentInformation,
  paymentTypes,
  checkoutB,
  milesHoldClosed,
}: {
  paymentType: number;
  paymentInformation?: OrderPaymentInformation;
  setPaymentInformation: (data: OrderPaymentInformation) => void;
  paymentTypes: PaymentType[];
  checkoutB?: boolean;
  milesHoldClosed?: boolean;
}) {
  const { toast } = useToast();
  const [isStarting, setIsStarting] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [cards, setCards] = useState<IyzicoSavedCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [showNewCard, setShowNewCard] = useState(false);
  const [threeDSHtml, setThreeDSHtml] = useState<string | null>(null);
  const threeDSSubmittedRef = useRef(false);
  const [userIsAnonymous, setUserIsAnonymous] = useState(true);
  const [registerCard, setRegisterCard] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [cardsFailMode, setCardsFailMode] = useState<'choose' | 'manual' | null>(null);
  const [cardsRetrying, setCardsRetrying] = useState(false);
  const [cardsLoading, setCardsLoading] = useState(false);

  const fullnamePreset = getCheckoutState()?.formFields?.[CheckoutFormField.SENDER_FULL_NAME] || '';

  useEffect(() => {
    setUserIsAnonymous(getIsAnonymous());
  }, []);

  useEffect(() => {
    async function startOrder() {
      if (milesHoldClosed || isStarting || paymentInformation?.conversationId) return;
      setIsStarting(true);
      try {
        const body = { paymentType, paymentInformation: {} };
        const { data: result } = await Requester.post<GenericResponse<OrderResponse>>(
          Endpoints.START_ORDER,
          body,
        );
        if (result.data.order.paymentInformation) {
          setPaymentInformation(result.data.order.paymentInformation);
        }
      } catch (error) {
        const { message } = ErrorHandler.getMessage(error);
        logIyzico(checkoutB, 'iyzico-start', message, 'api', error);
        toast({ variant: 'destructive', description: message });
      } finally {
        setIsStarting(false);
      }
    }
    startOrder();
  }, [paymentType, milesHoldClosed]);

  useEffect(() => {
    if (userIsAnonymous) {
      setShowNewCard(true);
      return;
    }
    if (getPreferManualCard(IYZICO_PREFER_MANUAL_CARD_KEY)) {
      setShowNewCard(true);
      setCardsFailMode('manual');
      return;
    }
    void loadCards();
  }, [userIsAnonymous]);

  async function loadCards(opts?: { fromRetry?: boolean }) {
    setCardsLoading(true);
    if (opts?.fromRetry) {
      setCardsRetrying(true);
    }
    try {
      const { data: result } = await Requester.get<GenericResponse<{ cards: IyzicoSavedCard[] }>>(
        Endpoints.IYZICO_CARDS,
      );
      const list = result.data?.cards || [];
      setCards(list);
      setPreferManualCard(IYZICO_PREFER_MANUAL_CARD_KEY, false);
      setCardsFailMode(null);
      if (list.length === 0) {
        setShowNewCard(true);
      } else {
        setSelectedCardId(list[0].id);
        setShowNewCard(false);
      }
    } catch (error) {
      logIyzico(checkoutB, 'iyzico-cards', 'Kayıtlı kartlar yüklenemedi.', 'unexpected', error);
      setShowNewCard(true);
      setCardsFailMode('manual');
    } finally {
      setCardsLoading(false);
      setCardsRetrying(false);
    }
  }

  function retryListSavedCards() {
    setPreferManualCard(IYZICO_PREFER_MANUAL_CARD_KEY, false);
    setCardsFailMode(null);
    void loadCards({ fromRetry: true });
  }

  function choosePayWithCardDetails() {
    setPreferManualCard(IYZICO_PREFER_MANUAL_CARD_KEY, true);
    setShowNewCard(true);
    setSelectedCardId(null);
    setCardsFailMode('manual');
  }

  async function removeCard(cardId: number) {
    setRemovingId(cardId);
    try {
      await Requester.post(Endpoints.IYZICO_CARDS_DELETE, { cardId });
      setCards((prev) => prev.filter((c) => c.id !== cardId));
      if (selectedCardId === cardId) {
        setSelectedCardId(null);
        setShowNewCard(true);
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      logIyzico(checkoutB, 'iyzico-remove-card', message, 'api', error);
      toast({ variant: 'destructive', description: message });
    } finally {
      setRemovingId(null);
    }
  }

  function trackPayment() {
    const basket = getBasket();
    setLastPaymentType('Kredi Kartı - iyzico');
    if (basket) {
      sendGTMEvent({
        event: GTMEventType.ADD_PAYMENT_INFO,
        ecommerce: {
          ...getBasketEcommerceBody(basket),
          payment_type: 'Kredi Kartı - iyzico',
        },
      });
    }
  }

  async function init3ds(body: Record<string, unknown>) {
    if (milesHoldClosed) return;
    if (!paymentInformation?.conversationId) {
      logIyzico(
        checkoutB,
        'iyzico-session',
        'Ödeme oturumu hazır değil. Lütfen yenileyin.',
        'unexpected',
      );
      toast({
        variant: 'destructive',
        description: 'Ödeme oturumu hazır değil. Lütfen yenileyin.',
      });
      return;
    }
    setIsPaying(true);
    trackPayment();
    rememberPendingOrderNumber(getBasket()?.orderNumber);
    try {
      const { data: result } = await Requester.post<
        GenericResponse<{ status: boolean; threeDSHtmlContent?: string }>
      >(Endpoints.IYZICO_3DS_INIT, body);
      const html = result.data?.threeDSHtmlContent;
      if (!result.data?.status || !html) {
        throw new Error('3DS başlatılamadı');
      }
      setThreeDSHtml(decodeThreeDSHtml(html));
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      logIyzico(checkoutB, 'iyzico-3ds', message, 'api', error);
      toast({ variant: 'destructive', description: message });
      setIsPaying(false);
    }
  }

  async function payWithSavedCard() {
    if (milesHoldClosed || !selectedCardId) return;
    await init3ds({ savedCardId: selectedCardId, installment: 1 });
  }

  async function payWithNewCard(values: {
    fullname: string;
    cardNumber: string;
    expirationMonth: string;
    expirationYear: string;
    cvv: string;
    cardAlias?: string;
  }) {
    if (milesHoldClosed) return;
    const wantRegister = !userIsAnonymous && registerCard;
    await init3ds({
      installment: 1,
      registerCard: wantRegister,
      card: {
        cardHolderName: values.fullname,
        cardNumber: values.cardNumber.replace(/\s/g, ''),
        expireMonth: values.expirationMonth,
        expireYear: values.expirationYear,
        cvc: values.cvv,
        cardAlias: values.cardAlias || 'Kredi Kartı',
      },
    });
  }

  useEffect(() => {
    if (!threeDSHtml || threeDSSubmittedRef.current) return;
    // ACS HTML window.onload ile submit eder; InnerHTML bunu çalıştırmaz ve form gizli kalır.
    const doc = new DOMParser().parseFromString(threeDSHtml, 'text/html');
    const form = doc.querySelector('form');
    if (!form) return;
    threeDSSubmittedRef.current = true;
    const imported = document.importNode(form, true) as HTMLFormElement;
    imported.style.display = 'none';
    document.body.appendChild(imported);
    // ACS formunda name="submit" input'u HTMLFormElement.submit'i ezer.
    HTMLFormElement.prototype.submit.call(imported);
  }, [threeDSHtml]);

  if (threeDSHtml) {
    return (
      <div className="w-full min-h-[420px] pt-4 flex flex-col items-center justify-center gap-3">
        <Spinner className="size-16" />
        <p className="text-sm text-hzc_beige_900">3D Secure sayfasına yönlendiriliyorsunuz...</p>
      </div>
    );
  }

  if (milesHoldClosed) return null;

  return (
    <div className="flex flex-col gap-4 sm:gap-6 justify-center pt-6 min-h-40 w-full max-w-lg mx-auto">
      {(isStarting || !paymentInformation?.conversationId || cardsLoading) && (
        <Spinner className="size-16 mx-auto" />
      )}

      <SavedCardsLoadFailedDialog
        open={cardsFailMode === 'choose'}
        retrying={cardsRetrying}
        onRetry={retryListSavedCards}
        onPayWithCard={choosePayWithCardDetails}
      />

      {!userIsAnonymous && cards.length > 0 && !showNewCard && cardsFailMode !== 'choose' && (
        <div className="flex flex-col gap-3 w-full">
          {cards.map((card) => (
            <div
              key={card.id}
              className={clsx(
                'flex items-center justify-between gap-3 border rounded-lg p-3 cursor-pointer',
                selectedCardId === card.id ? 'border-hzc_purple_500' : 'border-hzc_beige_300',
              )}
              onClick={() => setSelectedCardId(card.id)}
            >
              <div className="flex items-center gap-3">
                <SavedCardBrandIcon association={card.cardAssociation} />
                <div>
                  <div className="text-sm font-medium">{savedCardTitle(card)}</div>
                  <div className="text-xs text-hzc_beige_700">
                    {card.maskedPan || `**** **** **** ${card.lastFourDigits || ''}`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="p-2 text-hzc_beige_700 hover:text-red-600"
                disabled={removingId === card.id}
                onClick={(e) => {
                  e.stopPropagation();
                  void removeCard(card.id);
                }}
              >
                {removingId === card.id ? (
                  <Spinner className="size-4" />
                ) : (
                  <Trash className="size-4" />
                )}
              </button>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => {
              setShowNewCard(true);
              setSelectedCardId(null);
            }}
          >
            Yeni kart ile öde
          </Button>

          <Button
            type="button"
            className="rounded-full h-12"
            disabled={!selectedCardId || isPaying}
            onClick={() => void payWithSavedCard()}
          >
            {isPaying ? <Spinner className="size-5" /> : 'Ödemeyi Tamamla'}
          </Button>
        </div>
      )}

      {(showNewCard || userIsAnonymous || cards.length === 0) &&
        paymentInformation?.conversationId &&
        cardsFailMode !== 'choose' &&
        !cardsLoading && (
          <div className="w-full">
            {!userIsAnonymous && cards.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                className="mb-2 text-sm"
                onClick={() => {
                  setShowNewCard(false);
                  setSelectedCardId(cards[0]?.id ?? null);
                }}
              >
                Kayıtlı kartlara dön
              </Button>
            )}
            {!userIsAnonymous && (
              <div className="flex items-center gap-2 mb-3 text-sm text-hzc_beige_700">
                <Checkbox
                  checked={registerCard}
                  onCheckedChange={(v) => setRegisterCard(v === true)}
                />
                <span>Kartımı sonraki alışverişler için kaydet</span>
              </div>
            )}
            <MasterpassCardForm
              open={true}
              onOpenChange={() => {}}
              fullnamePreset={fullnamePreset}
              addingCard={false}
              forPayment={true}
              userIsAnonymous={true}
              setPaying={setIsPaying}
              paymentActionLabel={checkoutB ? 'Güvenli Ödeme Yap' : isPaying ? 'Ödeniyor...' : 'Ödemeyi Tamamla'}
              checkoutB={checkoutB}
              onSubmit={(values) => {
                void payWithNewCard(values);
              }}
            />
          </div>
        )}

      {checkoutB ? null : (
        <CheckoutPaymentProblem paymentType={paymentType} paymentTypes={paymentTypes} />
      )}
    </div>
  );
}
